import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import { 
    answerQuestion, 
    AnswerQuestionArgs, 
    getClients, 
    saveConclusion, 
    SaveConclusionArgs, 
    GetAnswersArgs, 
    getAnswers, 
    getSurvey, 
    SubmitSurveyArgs, 
    submitSurvey, 
    getConclusions, 
    GetConclusionsArgs } from "./api";
import { mutationErrorHandler, useToast } from "am-tax-fe-core";

export function useClients() {
    return useQuery({ ...queryKeys.clients.all, queryFn: getClients });
}

export function useAnswerQuestion() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (args: AnswerQuestionArgs) => {
            return answerQuestion(args);
        },
        onSuccess: async (_response) => {
            invalidateSurvey(queryClient, _response.id!);
        },
        ...mutationErrorHandler(toast, "Unable to save the response."),
    });
}

export function useSubmitSurvey() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (args: SubmitSurveyArgs) => {
            return submitSurvey(args);
        },
        onSuccess: async (_response, args: SubmitSurveyArgs) => {
            invalidateSurvey(queryClient, args.surveyId!);
        },
        ...mutationErrorHandler(toast, "Unable to submit the survey."),
    });
}

export function useSaveConclusion() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (args: SaveConclusionArgs) => {
            return saveConclusion(args)
        },
        onSuccess: async (_response, args: SaveConclusionArgs) => {
            invalidateSurvey(queryClient, args.surveyId);
        },
        ...mutationErrorHandler(toast, "Unable to save the conclusion."),
    });
}

export function useGetAnswers() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: async (args: GetAnswersArgs) => {
            const response = await getAnswers(args);
            //TO DO: Fix generation so we don't need to cast.
            const answers = (response?.answers as any).$values || []; // eslint-disable-line @typescript-eslint/no-explicit-any
            return answers;
        },
        onSuccess: async (_response, args: GetAnswersArgs) => {
            invalidateSurvey(queryClient, args.surveyId);
        },
        ...mutationErrorHandler(toast, "Unable to get answers for the given survey."),
    });
}

export function useGetSurvey() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => {
            return getSurvey();
        },
        onSuccess: async (_response) => {
            invalidateSurvey(queryClient, _response.id!);
        },
        onError: (error: unknown) => {
            console.error("Error fetching survey:", error);
        }
    });
}

export function useGetConclusions() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: GetConclusionsArgs) => {
            const response = await getConclusions(args);
            const conclusions = (response?.conclusions as any).$values || []; // eslint-disable-line @typescript-eslint/no-explicit-any
            return conclusions;
        },
        onSuccess: async (_response, args: GetConclusionsArgs) => {
            invalidateSurvey(queryClient, args.surveyId);
        },
        onError: (error: unknown) => {
            console.error("Error fetching survey conclusions:", error);
        }
    });
}


async function invalidateSurvey(queryClient: QueryClient, surveyId: string) {
    await queryClient.invalidateQueries(queryKeys.surveys.byId(surveyId));
}